import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "urql";
import { Link, useSearchParams } from "wouter";
import stableStringify from "json-stable-stringify";
import { Form, Formik } from "formik";
import { sortBy } from "lodash";

import { Button } from "../../../components/src/Button";
import { DateField, SearchListField, SubmitButton } from "../../../components/src/form";
import { ReactTable } from "../../../components/src/react-table/ReactTable";
import { SelectColumnFilter, SelectColumnFilterType } from "../../../components/src/react-table/ReactTableFilters";
import { twoDecPriceFormat, fourDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { DateHelper } from "../../../common/src";
import { BROKER_TRANSACTIONS_AND_CLIENTS_QUERY } from "../../../graphql/src/brokertransaction/queries";
import { BrokerTransaction, BrokerTransactionFilterInput, BrokerTransactionStatus, BrokerTransactionType } from "../types.generated";

interface FilterType {
    clientId: string;
    status: string;
    startDate: string;
    endDate: string;
}

export const BrokerTransactionsPage = (): React.ReactElement => {
    const [queryArgs, setQueryArgs] = useSearchParams();
    const [showFilter, setShowFilter] = useState(true);

    const defaultStartDate = DateHelper.addMonths(new Date(), -1).toISOString().slice(0, 10);
    const defaultEndDate = new Date().toISOString().slice(0, 10);

    const [filter, setFilter] = useState<FilterType>({
        clientId: null,
        status: null,
        startDate: defaultStartDate,
        endDate: defaultEndDate
    });

    useEffect(() => {
        const newFilter: FilterType = {
            clientId: null,
            status: null,
            startDate: defaultStartDate,
            endDate: defaultEndDate
        };

        if (queryArgs.get("clientId")) {
            newFilter.clientId = queryArgs.get("clientId");
        }
        if (queryArgs.get("status")) {
            newFilter.status = queryArgs.get("status");
        }
        if (queryArgs.get("startDate")) {
            newFilter.startDate = queryArgs.get("startDate");
        }
        if (queryArgs.get("endDate")) {
            newFilter.endDate = queryArgs.get("endDate");
        }

        if (stableStringify(filter) !== stableStringify(newFilter)) {
            setFilter(newFilter);
            setShowFilter(true);
        }
    }, [defaultEndDate, defaultStartDate, filter, queryArgs]);

    const brokerTransactionFilter: BrokerTransactionFilterInput = {
        clientIdIn: filter.clientId ? [filter.clientId] : null,
        statusIn: filter.status ? [filter.status as BrokerTransactionStatus] : null,
        startDate: filter.startDate || null,
        endDate: filter.endDate || null
    };

    const [{ fetching, error, data }] = useQuery<{
        brokerTransactions?: Partial<BrokerTransaction>[];
        clients?: Array<{ _id: string; name: string }>;
    }>({
        query: BROKER_TRANSACTIONS_AND_CLIENTS_QUERY,
        requestPolicy: "cache-and-network",
        variables: { filter: brokerTransactionFilter, includeVersions: false }
    });

    const columns = useMemo(
        () => [
            {
                header: "_id",
                accessorKey: "_id",
                cell: (cellProps) => {
                    return <Link to={`/brokertransactions/${cellProps.getValue()}`}>{cellProps.getValue()}</Link>;
                },
                meta: { className: "nowrap" },
                size: 220,
                minSize: 180
            },
            {
                header: "Client",
                id: "client.name",
                accessorKey: "client.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.client) {
                        return <Link to={`/parties/${row.original.client._id}`}>{cellProps.getValue()}</Link>;
                    }
                    return null;
                },
                filter: SelectColumnFilter,
                meta: { className: "nowrap" },
                size: 200,
                minSize: 140
            },
            {
                header: "Broker",
                id: "broker.name",
                accessorKey: "broker.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.broker) {
                        return <Link to={`/parties/${row.original.broker._id}`}>{cellProps.getValue()}</Link>;
                    }
                    return null;
                },
                filter: SelectColumnFilter,
                meta: { className: "nowrap" },
                size: 70,
                minSize: 55
            },
            {
                header: "Type",
                accessorKey: "type",
                filter: SelectColumnFilterType(BrokerTransactionType),
                meta: { className: "nowrap" },
                size: 55,
                minSize: 50
            },
            {
                header: "Status",
                accessorKey: "status",
                filter: SelectColumnFilterType(BrokerTransactionStatus),
                meta: { className: "nowrap" },
                size: 85,
                minSize: 75
            },
            {
                header: "Description",
                accessorKey: "description",
                size: 120,
                minSize: 80
            },
            {
                header: "Instrument",
                id: "instrument.name",
                accessorKey: "instrument.name",
                cell: (cellProps) => {
                    const { row } = cellProps;
                    if (row.original.instrument) {
                        return <Link to={`/instruments/${row.original.instrument._id}`}>{cellProps.getValue()}</Link>;
                    }
                    return null;
                },
                filter: SelectColumnFilter,
                meta: { className: "nowrap" },
                size: 110,
                minSize: 80
            },
            {
                header: "Quantity",
                accessorKey: "quantity",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{fourDecPriceFormat(cellProps.getValue())}</div>,
                meta: { className: "nowrap right" },
                size: 90,
                minSize: 75
            },
            {
                header: "Price",
                accessorKey: "price",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{fourDecPriceFormat(cellProps.getValue())}</div>,
                meta: { className: "nowrap right" },
                size: 90,
                minSize: 75
            },
            {
                header: "Commission",
                accessorKey: "commission",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>,
                meta: { className: "nowrap right" },
                size: 80,
                minSize: 70
            },
            {
                header: "Stamp duty",
                accessorKey: "stampDuty",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>,
                meta: { className: "nowrap right" },
                size: 80,
                minSize: 70
            },
            {
                header: "Settlement amount",
                accessorKey: "settlementAmount",
                cell: (cellProps) => <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>,
                meta: { className: "nowrap right" },
                size: 120,
                minSize: 100
            },
            {
                header: "Currency",
                accessorKey: "currency",
                filter: SelectColumnFilter,
                meta: { className: "nowrap" },
                size: 55,
                minSize: 50
            },
            {
                header: "Trade date",
                accessorKey: "tradeDate",
                meta: { className: "nowrap" },
                size: 95,
                minSize: 90
            },
            {
                header: "Value date",
                accessorKey: "valueDate",
                meta: { className: "nowrap" },
                size: 95,
                minSize: 90
            },
            {
                header: "External id",
                accessorKey: "externalId",
                meta: { className: "nowrap" },
                size: 140,
                minSize: 110
            },
            {
                header: "Error",
                accessorKey: "error",
                size: 120,
                minSize: 80
            },
            {
                header: "Transaction",
                accessorKey: "correspondingTransactionId",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (value) {
                        return <Link to={`/transactions/minitransaction/${value}`}>{value}</Link>;
                    }
                    return null;
                },
                meta: { className: "nowrap" },
                size: 220,
                minSize: 180
            },
            {
                header: "Update timestamp",
                accessorKey: "updateTimestamp",
                meta: { className: "nowrap" },
                size: 145,
                minSize: 120
            }
        ],
        []
    );

    const sortedTransactions = useMemo(() => {
        if (!data?.brokerTransactions) return [];
        return sortBy(data.brokerTransactions, "tradeDate").reverse();
    }, [data?.brokerTransactions]);

    if (fetching) return <div>Loading...</div>;
    if (error)
        return (
            <div>
                <p>error:</p>
                <pre>{JSON.stringify(error, null, 2)}</pre>
            </div>
        );

    const clients = data?.clients ? sortBy(data.clients, "name") : [];
    const statuses = Object.values(BrokerTransactionStatus).map((status) => ({ _id: status, name: status }));

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">Number of broker transactions: {sortedTransactions.length}</div>
            </div>

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4 pl-0">
                    {showFilter ? (
                        <Button
                            variant="ghost"
                            className="underline text-sky-600 hover:text-sky-800 border-0 p-0"
                            onClick={() => setShowFilter(false)}
                        >
                            Hide filter
                        </Button>
                    ) : (
                        <Button
                            variant="ghost"
                            className="underline text-sky-600 hover:text-sky-800 border-0 p-0"
                            onClick={() => setShowFilter(true)}
                        >
                            Show filter
                        </Button>
                    )}
                </div>
            </div>

            {showFilter ? (
                <div className="flex flex-wrap -mx-2">
                    <div className="w-full px-2 mb-4">
                        <Formik
                            enableReinitialize={true}
                            initialValues={filter}
                            onSubmit={async (submitValues) => {
                                setQueryArgs((prev) => {
                                    if (submitValues.clientId) {
                                        prev.set("clientId", submitValues.clientId);
                                    } else {
                                        prev.delete("clientId");
                                    }
                                    if (submitValues.status) {
                                        prev.set("status", submitValues.status);
                                    } else {
                                        prev.delete("status");
                                    }
                                    if (submitValues.startDate) {
                                        prev.set("startDate", submitValues.startDate);
                                    } else {
                                        prev.delete("startDate");
                                    }
                                    if (submitValues.endDate) {
                                        prev.set("endDate", submitValues.endDate);
                                    } else {
                                        prev.delete("endDate");
                                    }
                                    return prev;
                                });
                            }}
                        >
                            {({ isSubmitting }) => (
                                <Form autoComplete="off">
                                    <div className="flex flex-wrap -mx-2">
                                        <div className="w-full md:w-3/12 px-2 mb-4">
                                            <SearchListField
                                                name="clientId"
                                                label="Client"
                                                className=""
                                                disabled={isSubmitting}
                                                options={clients}
                                            />
                                        </div>
                                        <div className="w-full md:w-3/12 px-2 mb-4">
                                            <SearchListField
                                                name="status"
                                                label="Status"
                                                className=""
                                                disabled={isSubmitting}
                                                options={statuses}
                                            />
                                        </div>
                                        <div className="w-full md:w-3/12 px-2 mb-4">
                                            <DateField name="startDate" label="Start date" className="" disabled={isSubmitting} />
                                        </div>
                                        <div className="w-full md:w-3/12 px-2 mb-4">
                                            <DateField name="endDate" label="End date" className="" disabled={isSubmitting} />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap -mx-2 mb-4">
                                        <div className="w-full md:w-4/12 px-2 mb-4">
                                            <SubmitButton disabled={fetching} label="Apply" />
                                        </div>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            ) : null}

            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4">
                    <ReactTable
                        columns={columns}
                        data={sortedTransactions}
                        defaultHiddenColumns={["_id", "description", "error", "correspondingTransactionId", "updateTimestamp"]}
                    />
                </div>
            </div>
        </div>
    );
};
